/**
 * Minimal 3D math for the WebGL floor-plan view: column-major 4×4 matrices
 * (WebGL convention) and the handful of vec3 ops the camera + culling need.
 * Kept dependency-free and local to the 3D layer.
 */

export type Vec3 = [number, number, number];
export type Mat4 = Float32Array;

export const v3 = (x: number, y: number, z: number): Vec3 => [x, y, z];

export function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
export function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}
export function scale(a: Vec3, s: number): Vec3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}
export function dot3d(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}
export function length(a: Vec3): number {
  return Math.hypot(a[0], a[1], a[2]);
}
export function normalize(a: Vec3): Vec3 {
  const l = length(a) || 1e-9;
  return [a[0] / l, a[1] / l, a[2] / l];
}

/**
 * How far along the ray from `origin` in direction `direction` it meets the
 * plane through `planePoint` with normal `planeNormal`, in units of
 * `direction`'s length; null when the ray runs along the plane or meets it
 * behind its origin.
 */
export function rayPlaneDistance(origin: Vec3, direction: Vec3, planePoint: Vec3, planeNormal: Vec3): number | null {
  const denominator = dot3d(direction, planeNormal);
  if (Math.abs(denominator) < 1e-9) return null;
  const distance = dot3d(sub(planePoint, origin), planeNormal) / denominator;
  return distance > 0 ? distance : null;
}

/** Identity matrix. */
export function identity(): Mat4 {
  const m = new Float32Array(16);
  m[0] =
    m[5] =
    m[10] =
    m[15] =
      1;
  return m;
}

/** out = a · b  (both column-major). */
export function multiply(a: Mat4, b: Mat4): Mat4 {
  const o = new Float32Array(16);
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      o[c * 4 + r] = a[r] * b[c * 4]
        + a[4 + r] * b[c * 4 + 1]
        + a[8 + r] * b[c * 4 + 2]
        + a[12 + r] * b[c * 4 + 3];
    }
  }
  return o;
}

/** Perspective projection (right-handed, looking down -Z), column-major. */
export function perspective(fovyRad: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovyRad / 2);
  const nf = 1 / (near - far);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = (far + near) * nf;
  m[11] = -1;
  m[14] = 2 * far * near * nf;
  return m;
}

/** Right-handed look-at view matrix, column-major. */
export function lookAt(eye: Vec3, center: Vec3, up: Vec3): Mat4 {
  const z = normalize(sub(eye, center)); // forward (points back toward eye)
  const x = normalize(cross(up, z)); // right
  const y = cross(z, x); // true up
  const m = new Float32Array(16);
  m[0] = x[0];
  m[4] = x[1];
  m[8] = x[2];
  m[12] = -dot3d(x, eye);
  m[1] = y[0];
  m[5] = y[1];
  m[9] = y[2];
  m[13] = -dot3d(y, eye);
  m[2] = z[0];
  m[6] = z[1];
  m[10] = z[2];
  m[14] = -dot3d(z, eye);
  m[3] = 0;
  m[7] = 0;
  m[11] = 0;
  m[15] = 1;
  return m;
}
